import { useEffect, useRef, useState } from 'react'
import {
  HARBOUR_ADOPTION_GAPS,
  HARBOUR_SEARCH_DEBOUNCE_MS,
  HARBOUR_SEARCH_LIMIT,
  HARBOUR_SEARCH_MIN_CHARS,
} from 'src/constants/harbour'
import { searchHarbourVenues } from 'src/utils/harbourApi'

const EMPTY_RESULT = {
  venues: [],
  loading: false,
  error: null,
  completedQuery: '',
  completedGap: '',
}

function isHarbourSearchGap(value) {
  return HARBOUR_ADOPTION_GAPS.includes(value)
}

/**
 * Cheap Harbour name / paid-gap search. Cap is the product (20).
 * Not a Spartacus list. Gap chips are session-only.
 */
export function useHarbourSearch({ enabled = true } = {}) {
  const [query, setQuery] = useState('')
  const [gap, setGap] = useState('')
  const [result, setResult] = useState(EMPTY_RESULT)
  const requestIdRef = useRef(0)

  useEffect(() => {
    const search = String(query || '').trim()
    const facet = isHarbourSearchGap(gap) ? gap : ''
    if (!enabled || (!facet && search.length < HARBOUR_SEARCH_MIN_CHARS)) {
      requestIdRef.current += 1
      setResult(EMPTY_RESULT)
      return undefined
    }

    const timeoutId = setTimeout(() => {
      loadSearch()
    }, HARBOUR_SEARCH_DEBOUNCE_MS)

    async function loadSearch() {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      setResult(prev => ({ ...prev, loading: true, error: null }))
      try {
        const searchResult = await searchHarbourVenues({
          ...(search ? { search } : {}),
          ...(facet ? { gap: facet } : {}),
        })
        if (requestIdRef.current !== requestId) {
          return
        }
        setResult({
          venues: (searchResult.venues || []).slice(0, HARBOUR_SEARCH_LIMIT),
          loading: false,
          error: null,
          completedQuery: search,
          completedGap: facet,
        })
      } catch (err) {
        if (requestIdRef.current !== requestId) {
          return
        }
        setResult({
          venues: [],
          loading: false,
          error: err.message || 'Failed to search Harbour venues',
          completedQuery: search,
          completedGap: facet,
        })
      }
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [query, gap, enabled])

  const { venues, loading, error, completedQuery, completedGap } = result
  const search = String(query || '').trim()
  const facet = isHarbourSearchGap(gap) ? gap : ''
  const completed =
    completedQuery === search &&
    completedGap === facet &&
    Boolean(search || facet)
  const emptyComplete = completed && !loading && venues.length === 0 && !error
  const missed = emptyComplete && !facet

  return {
    query,
    setQuery,
    gap: facet,
    setGap,
    venues,
    loading,
    error,
    missed,
    emptyComplete,
    completedQuery,
    completedGap,
  }
}
